import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ApiService from "../../services/ApiService";
import { useUpdateComponentsJob } from "../../context/UpdateComponentsJobContext";
import Spinner from "@cs/components/src/Spinner";
import { useNavigate, useParams } from "react-router-dom";
import Banner from "@cs/components/src/Banner";
import VariableInputForm from "../shared/VariableInputForm";
import { Trash2, Save, Settings, XCircle } from "react-feather";
import Button from "@cs/components/src/Button";
import Checkbox from "@cs/components/src/Checkbox";
import JobList from "../jobs/JobList";
import { styled } from "styled-components";
import PluginInstanceComponentList from "./PluginInstanceComponentList";
import Section from "@cs/components/src/Section";
import Tabs from "@cs/components/src/Tabs";
import { Status } from "./PluginInstanceEditScreen";

interface Props {
  editedPluginInstances: Record<string, PluginInstanceItem>;
  onChange: (store: PluginInstanceItem) => void;
  onRemove: (id: string) => void;
  onStatusChange: (id: string, status: string, message?: string) => void;
  statuses: Record<string, Status>;
}

let rememberedTab = "settings";

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  > * + * {
    margin-left: 5px;
  }
`;

const DevModeWrapper = styled.div`
  padding: 10px 15px 0;
`;

const PluginInstanceForm = (props: Props) => {
  const {
    onChange,
    onStatusChange,
    editedPluginInstances: pluginInstances,
    onRemove,
    statuses,
  } = props;
  const { pluginId, systemDomainId } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addComponentsJob } = useUpdateComponentsJob();
  const [activeTab, setActiveTab] = useState(rememberedTab);

  const editedPluginInstance = pluginInstances[pluginId ?? ""];
  const [doRemove, setDoRemove] = useState(!editedPluginInstance);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["pluginInstance", systemDomainId, pluginId],
    queryFn: async () => {
      if (editedPluginInstance) {
        const response = await ApiService.fetch<{
          data: PluginConfigItem;
        }>("GET", `/api/config/plugin/${editedPluginInstance.pluginConfigId}`);
        return { pluginConfig: response.data, pluginInstance: undefined };
      } else {
        const response = await ApiService.fetch<{
          data: {
            pluginConfig: PluginConfigItem;
            pluginInstance: PluginInstanceItem;
          };
        }>("GET", `/api/plugin_instance/${systemDomainId}/${pluginId}`);
        return response.data;
      }
    },
  });

  const pluginInstance = editedPluginInstance ?? data?.pluginInstance;
  const pluginConfig = data?.pluginConfig;

  const saveMutation = useMutation({
    mutationFn: async () => {
      setDoRemove(true);
      const res = await ApiService.fetch<{
        scriptExecutionId: string;
        systemDomainId: string;
      }>(
        "PUT",
        `/api/plugin_instance/${systemDomainId}/${pluginInstance.id}`,
        pluginInstance
      );
      queryClient.invalidateQueries({
        queryKey: ["pluginInstanceList", systemDomainId],
      });
      return res;
    },
    onError: () =>
      onStatusChange(pluginInstance.id, "error", "Error saving plugin"),
    onSuccess: (data) => {
      onStatusChange(pluginInstance.id, "success");
      addComponentsJob(
        data.scriptExecutionId,
        data.systemDomainId,
        "Updating components"
      );
    },
  });

  const abortAllMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch(
        "POST",
        `/api/script_execution/abort/all/${systemDomainId}/${pluginInstance?.id}`
      );
      queryClient.invalidateQueries({ queryKey: ["jobList"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await ApiService.fetch<{
        scriptExecutionId: string;
        systemDomainId: string;
      }>(
        "DELETE",
        `/api/plugin_instance/${systemDomainId}/${pluginInstance.id}`
      );
      queryClient.invalidateQueries({
        queryKey: ["pluginInstanceList", systemDomainId],
      });
      onRemove(pluginInstance.id);
      navigate(`..`);
      return res;
    },
    onError: () =>
      onStatusChange(pluginInstance.id, "error", "Error removing plugin"),
    onSuccess: (data) =>
      addComponentsJob(
        data.scriptExecutionId,
        data.systemDomainId,
        "Updating components"
      ),
  });

  const handleTabChange = (tab: string) => {
    rememberedTab = tab;
    setActiveTab(tab);
  };

  const isReady = !isLoading && !isError && !!pluginConfig && !!pluginInstance;
  const status = pluginInstance ? statuses[pluginInstance.id] : undefined;

  const hasRequiredFieldsMissing =
    isReady &&
    pluginConfig.variables.some((variable) => {
      if (!variable.required) {
        return false;
      }
      const variableId = variable.id ?? variable.key;
      const instance = pluginInstance.variables.find(
        ({ variableConfigId, dataType }) =>
          variableConfigId === variableId && dataType === variable.dataType
      );
      const value = instance?.value;
      if (value === undefined || value === null || value === "") {
        return true;
      }
      if (Array.isArray(value) && value.length === 0) {
        return true;
      }
      return false;
    });

  return (
    <Section
      title={
        isReady ? (
          <TitleRow>
            <span>{pluginConfig.title}</span>
            <Button
              icon={Settings}
              borderless
              type="default"
              link={`/config/plugin/${pluginConfig.id}`}
            />
          </TitleRow>
        ) : (
          "Loading..."
        )
      }
      subTitle={
        <Tabs
          tabs={[
            {
              key: "settings",
              label: "Settings",
              color: hasRequiredFieldsMissing ? "var(--orange300)" : undefined,
            },
            { key: "components", label: "Components" },
            { key: "jobs", label: "Jobs" },
          ]}
          value={activeTab}
          onChange={isReady ? handleTabChange : undefined}
        />
      }
      buttons={
        <>
          {isReady && (
            <Banner
              error={status?.status === "error" ? status.message : false}
              warn={status?.status === "dirty"}
              success={status?.status === "success"}
            />
          )}
          <Button
            label="Remove"
            icon={Trash2}
            type="danger"
            borderless
            disabled={!isReady}
            onClick={() => {
              if (doRemove) {
                deleteMutation.mutate();
              } else {
                onRemove(pluginInstance.id);
                navigate("..");
              }
            }}
            loading={deleteMutation.isLoading}
          />
          <Button
            label="Save"
            icon={Save}
            type="success"
            disabled={!isReady}
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isLoading}
          />
        </>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <Banner error="Error loading" />
      ) : isReady ? (
        <>
          {activeTab === "settings" && (
            <>
              <DevModeWrapper>
                <Checkbox
                  inlineLabel
                  label="Development Mode"
                  value={pluginInstance.development ?? false}
                  onChange={(value) => {
                    onStatusChange(pluginInstance.id, "dirty");
                    onChange({ ...pluginInstance, development: value });
                  }}
                />
              </DevModeWrapper>
              <VariableInputForm
                value={pluginInstance}
                config={pluginConfig}
                onChange={(value) => {
                  onStatusChange(pluginInstance.id, "dirty");
                  onChange(value);
                }}
              />
            </>
          )}
          {activeTab === "components" && (
            <PluginInstanceComponentList
              pluginConfig={pluginConfig}
              pluginInstance={pluginInstance}
              onChange={(value) => {
                onStatusChange(pluginInstance.id, "dirty");
                onChange(value);
              }}
            />
          )}
          {activeTab === "jobs" && (
            <>
              {abortAllMutation.isError && (
                <Banner error="Failed to abort all jobs" />
              )}
              <Button
                label="Abort All"
                type="danger"
                icon={XCircle}
                borderless
                onClick={() => abortAllMutation.mutate()}
                loading={abortAllMutation.isLoading}
              />
              <JobList
                systemDomainId={systemDomainId}
                instanceId={pluginInstance.id}
                modal
              />
            </>
          )}
        </>
      ) : null}
    </Section>
  );
};

export default PluginInstanceForm;
